/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* ------------------------------------------------------------------------- *
 *  Size unit toggle (EU / US / CM / UK)
 *
 *  Two sources of truth per convertible element:
 *    1. Per-element unit map, built either from the product's variant data
 *       (product page) or from the line item's own option values (cart).
 *    2. size-conversions.json — brand-keyed fallback used for units the
 *       product itself doesn't carry as a variant option.
 *
 *  Chosen unit lives in localStorage under `theme:size-unit`; default `eu`.
 *
 *  Convertible elements:
 *    - [data-primary-value]     product-page swatches + selected-value span
 *    - [data-line-item-map]     cart / order line-item property spans
 * ------------------------------------------------------------------------- */

const SIZE_UNITS = ['eu', 'us', 'cm', 'uk'];
const SIZE_UNIT_STORAGE_KEY = 'theme:size-unit';

let _sizeConversionsPromise = null;
function loadSizeConversions() {
  if (!_sizeConversionsPromise) {
    const url = window.themeVariables?.libs?.sizeConversions;
    _sizeConversionsPromise = fetch(url)
      .then((r) => r.json())
      .catch(() => ({ default: {} }));
  }
  return _sizeConversionsPromise;
}

function readSizeUnit() {
  let stored;
  try {
    stored = localStorage.getItem(SIZE_UNIT_STORAGE_KEY);
  } catch (e) {
    stored = null;
  }
  return SIZE_UNITS.includes(stored) ? stored : 'eu';
}

function writeSizeUnit(unit) {
  try {
    localStorage.setItem(SIZE_UNIT_STORAGE_KEY, unit);
  } catch (e) {
    // Safari private mode — preference simply won't persist.
  }
}

function safeJSONParse(str, fallback) {
  try {
    return str ? JSON.parse(str) : fallback;
  } catch (e) {
    return fallback;
  }
}

function brandLookup(table, vendor, eu, unit) {
  const b = table[vendor] || table.default || {};
  const v = b[eu] && b[eu][unit];
  return v != null && v !== '' ? v : null;
}

// Reverse lookup: given a non-EU unit value, find the EU key that maps to it.
// Used when the primary axis isn't EU (e.g. Nike Tiempo has only UK+US) and
// we need to bridge to EU via the JSON table.
function reverseBrandLookup(table, vendor, srcUnit, srcVal) {
  const b = table[vendor] || table.default || {};
  for (const eu of Object.keys(b)) {
    if (b[eu] && b[eu][srcUnit] === srcVal) return eu;
  }
  return null;
}

function convertOne(el, unit, table) {
  const map = el._unitMap || {};
  const vendor = el._vendor;
  const primary = el.dataset.primaryValue;

  // 1. Directly present in the per-element map — cheapest and most accurate.
  if (map[unit] != null) return map[unit];

  // 2. Forward via JSON table using the map's EU value.
  if (map.eu != null) {
    const mapped = unit === 'eu' ? map.eu : brandLookup(table, vendor, map.eu, unit);
    if (mapped != null) return mapped;
  }

  // 3. Reverse via JSON table: find EU for any other unit in the map, then forward.
  for (const srcUnit of Object.keys(map)) {
    if (srcUnit === 'eu') continue;
    const foundEu = reverseBrandLookup(table, vendor, srcUnit, map[srcUnit]);
    if (foundEu == null) continue;
    if (unit === 'eu') return foundEu;
    const mapped = brandLookup(table, vendor, foundEu, unit);
    if (mapped != null) return mapped;
  }

  // 4. Give up — show the primary value unchanged.
  return primary != null ? primary : el.textContent;
}

function relabelAll(unit, table) {
  document.querySelectorAll('[data-primary-value], [data-line-item-map]').forEach((el) => {
    el.textContent = convertOne(el, unit, table);
  });
  document.querySelectorAll('[data-size-name-display]').forEach((el) => {
    const base = el.dataset.sizeNameBase || 'Size';
    el.textContent = `${base} (${unit.toUpperCase()}):`;
  });
  document.querySelectorAll('size-unit-toggle').forEach((toggle) => {
    toggle.querySelectorAll('button[data-unit]').forEach((b) => {
      b.setAttribute('aria-pressed', b.dataset.unit === unit ? 'true' : 'false');
    });
  });
}

function attachProductPageMaps(picker) {
  const axisUnits = safeJSONParse(
    picker.querySelector('script[data-size-axis-units]')?.textContent,
    { 1: null, 2: null, 3: null }
  );
  const variants = safeJSONParse(
    picker.querySelector('script[data-size-variants]')?.textContent,
    []
  );
  const primaryPos = parseInt(picker.dataset.sizePrimaryPosition, 10);
  const vendor = picker.dataset.vendor;

  picker._axisUnits = axisUnits;
  picker._variants = variants;
  picker._primaryPos = primaryPos;
  picker._vendor = vendor;

  picker.querySelectorAll('[data-primary-value]').forEach((el) => {
    const pv = el.dataset.primaryValue;
    const variant = variants.find((v) => v[primaryPos] === pv); // [id, opt1, opt2, opt3]
    const map = {};
    if (variant) {
      for (const posStr of ['1', '2', '3']) {
        const u = axisUnits[posStr];
        if (u && u !== 'unknown') map[u] = variant[parseInt(posStr, 10)];
      }
    }
    el._unitMap = map;
    el._vendor = vendor;
  });
}

function attachCartMaps(root = document) {
  root.querySelectorAll('[data-line-item-map]').forEach((el) => {
    el._unitMap = safeJSONParse(el.getAttribute('data-line-item-map'), {});
    el._vendor = el.dataset.vendor;
  });
}

function pruneToggleButtons(toggle, table) {
  const picker = toggle.closest('variant-picker');
  if (!picker) return;

  const swatches = picker.querySelectorAll(
    '.product-form__option-selector[data-is-primary-size-axis] [data-primary-value]'
  );
  const supported = new Set();

  for (const swatch of swatches) {
    const map = swatch._unitMap || {};
    Object.keys(map).forEach((u) => supported.add(u));

    // Try to bridge to EU (needed to unlock the JSON conversion table).
    let euValue = map.eu;
    if (!euValue) {
      for (const srcUnit of Object.keys(map)) {
        const foundEu = reverseBrandLookup(table, picker._vendor, srcUnit, map[srcUnit]);
        if (foundEu != null) {
          euValue = foundEu;
          supported.add('eu');
          break;
        }
      }
    }

    // Any unit the brand table can produce from that EU value is supported.
    if (euValue) {
      const b = table[picker._vendor] || table.default || {};
      if (b[euValue]) Object.keys(b[euValue]).forEach((u) => supported.add(u));
    }
  }

  toggle.querySelectorAll('button[data-unit]').forEach((btn) => {
    if (!supported.has(btn.dataset.unit)) btn.setAttribute('hidden', '');
    else btn.removeAttribute('hidden');
  });
}

class SizeUnitToggle extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const btn = event.target.closest('button[data-unit]');
      if (!btn || btn.hasAttribute('hidden')) return;
      const unit = btn.dataset.unit;
      writeSizeUnit(unit);
      loadSizeConversions().then((table) => relabelAll(unit, table));
    });
  }
}
customElements.define('size-unit-toggle', SizeUnitToggle);

// The block-swatch radio's `value` attribute holds Shopify's option-value ID
// (e.g. 8333261013329), NOT the human-readable size string. To recover the
// size we go via the associated label (linked by for="…") and read either
// its `data-primary-value` (primary axis) or its raw textContent (hidden
// axes — those aren't touched by the toggle so textContent stays raw).
function labelForRadio(radio) {
  if (!radio.id) return null;
  return document.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
}

function labelValue(label) {
  if (!label) return null;
  if (label.dataset && label.dataset.primaryValue != null) return label.dataset.primaryValue;
  return label.textContent.trim();
}

function primaryValueForRadio(radio) {
  return labelValue(labelForRadio(radio));
}

// Capture-phase listener: sync hidden size-family radios BEFORE the theme's
// bubble-phase handler recomputes the variant. This turns a click on the
// primary picker into a valid multi-axis variant selection.
document.addEventListener(
  'change',
  (event) => {
    const radio = event.target;
    if (!radio || !radio.matches || !radio.matches('input[type="radio"][data-option-position]')) return;

    const primary = radio.closest('.product-form__option-selector[data-is-primary-size-axis]');
    if (!primary) return;

    const picker = radio.closest('variant-picker');
    if (!picker || !picker._variants) return;

    const primaryValue = primaryValueForRadio(radio);
    if (primaryValue == null) return;

    const variant = picker._variants.find((v) => v[picker._primaryPos] === primaryValue);
    if (!variant) {
      // eslint-disable-next-line no-console
      console.warn(
        '[size-unit-toggle] no variant matches primary value',
        { primaryValue, primaryPos: picker._primaryPos }
      );
      return;
    }

    // Pre-resolve every hidden axis's target radio. If any axis can't find
    // its target (e.g. a mismatch between the variant JSON value and the
    // rendered label textContent), abort the sync entirely — mutating some
    // axes and leaving others stale would produce an invalid combination
    // and crash the theme's variant handler on a null variant.
    const syncOps = [];
    let syncOK = true;
    picker
      .querySelectorAll('.product-form__option-selector[data-size-family]')
      .forEach((sel) => {
        if (sel.hasAttribute('data-is-primary-size-axis')) return;
        const radios = sel.querySelectorAll('input[data-option-position]');
        if (!radios.length) return;
        const targetValue = variant[parseInt(radios[0].dataset.optionPosition, 10)];
        let targetRadio = null;
        radios.forEach((r) => {
          if (targetRadio == null && labelValue(labelForRadio(r)) === targetValue) {
            targetRadio = r;
          }
        });
        if (targetRadio == null) {
          syncOK = false;
          // eslint-disable-next-line no-console
          console.warn(
            '[size-unit-toggle] no matching label for hidden axis',
            { position: radios[0].dataset.optionPosition, targetValue }
          );
          return;
        }
        syncOps.push({ radios, targetRadio });
      });

    if (!syncOK) return;
    for (const { radios, targetRadio } of syncOps) {
      radios.forEach((r) => {
        r.checked = r === targetRadio;
      });
    }
  },
  true
);

// Product-page: after the theme processes a variant change, refresh the
// selected-value display so the current unit label re-renders correctly.
// Note: radio.value is the option-value ID (e.g. 8333261013329), NOT the
// size string, so we resolve the primary value via the associated label.
document.addEventListener('variant:changed', async () => {
  const table = await loadSizeConversions();

  document.querySelectorAll('variant-picker[data-has-size-primary]').forEach((picker) => {
    const primaryOptionSelector = picker.querySelector(
      '.product-form__option-selector[data-is-primary-size-axis]'
    );
    if (!primaryOptionSelector) return;
    const checked = primaryOptionSelector.querySelector('input[type="radio"]:checked');
    if (!checked) return;

    const newPrimary = primaryValueForRadio(checked);
    if (newPrimary == null) return;

    const display = picker.querySelector('[data-size-value-display]');
    if (!display) return;

    display.dataset.primaryValue = newPrimary;

    // Prefer the pre-computed map on the freshly checked swatch label — it
    // already knows this variant's other unit values.
    const label = labelForRadio(checked);
    if (label && label._unitMap) {
      display._unitMap = label._unitMap;
      display._vendor = label._vendor;
    } else {
      const variant = picker._variants?.find((v) => v[picker._primaryPos] === newPrimary);
      const map = {};
      if (variant) {
        for (const posStr of ['1', '2', '3']) {
          const u = picker._axisUnits?.[posStr];
          if (u && u !== 'unknown') map[u] = variant[parseInt(posStr, 10)];
        }
      }
      display._unitMap = map;
      display._vendor = picker._vendor;
    }
  });

  relabelAll(readSizeUnit(), table);
});

// Cart re-renders — rebind maps then relabel.
document.addEventListener('cart:updated', async () => {
  const table = await loadSizeConversions();
  attachCartMaps();
  relabelAll(readSizeUnit(), table);
});
document.addEventListener('cart:refresh', async () => {
  const table = await loadSizeConversions();
  attachCartMaps();
  relabelAll(readSizeUnit(), table);
});

// Product-page section rerenders: after Focal's <product-rerender> replaces
// the variant-picker DOM with fresh server HTML, every JS property we set on
// the old nodes (_unitMap, _variants, _vendor) is gone. Re-attach on the new
// nodes and re-apply the current unit.
//
// The theme dispatches `product:rerender` WITHOUT bubbles: true, so a normal
// document listener never fires. We use capture phase (which runs regardless
// of bubbles) and defer to a microtask so the theme's own target-phase
// listener has already swapped the DOM by the time we run.
document.addEventListener(
  'product:rerender',
  () => {
    Promise.resolve().then(async () => {
      const table = await loadSizeConversions();
      document
        .querySelectorAll('variant-picker[data-has-size-primary]')
        .forEach(attachProductPageMaps);
      document
        .querySelectorAll('size-unit-toggle')
        .forEach((toggle) => pruneToggleButtons(toggle, table));
      relabelAll(readSizeUnit(), table);
    });
  },
  true
);

// Attach maps synchronously so `_variants` and `_unitMap` are populated
// before any user interaction — otherwise an early click races against the
// async fetch below and the sync bails on missing state.
function initSizeUnitMapsSync() {
  document.querySelectorAll('variant-picker[data-has-size-primary]').forEach(attachProductPageMaps);
  attachCartMaps();
}

async function initSizeUnitFeature() {
  initSizeUnitMapsSync();
  const table = await loadSizeConversions();
  document
    .querySelectorAll('size-unit-toggle')
    .forEach((toggle) => pruneToggleButtons(toggle, table));
  relabelAll(readSizeUnit(), table);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSizeUnitMapsSync);
  document.addEventListener('DOMContentLoaded', initSizeUnitFeature);
} else {
  initSizeUnitMapsSync();
  initSizeUnitFeature();
}